import React, {useEffect, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'
import {restRequest} from '../../../api-utils/apiQueries'
import {
  getRangeForPreset,
  GiftCardDatePreset,
  GiftCardFilterState,
  GIFT_CARD_DATE_PRESET_OPTIONS,
  toDateInputValue,
} from './giftCardDateRange'

type Props = {
  showFilter: boolean
  toggle: () => void
  activeFilter: GiftCardFilterState
  onApply: (filter: GiftCardFilterState) => void
}

const GiftCardFilter = ({showFilter, toggle, activeFilter, onApply}: Props) => {
  const [preset, setPreset] = useState<GiftCardDatePreset>(activeFilter.preset)
  const [customFrom, setCustomFrom] = useState(activeFilter.date_from)
  const [customTo, setCustomTo] = useState(activeFilter.date_to)
  const [categories, setCategories] = useState<{value: string; label: string}[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>(activeFilter.category)
  const [isAllChecked, setIsAllChecked] = useState(false)
  const [rangeError, setRangeError] = useState('')
  const [loadingCategories, setLoadingCategories] = useState(false)

  useEffect(() => {
    if (!showFilter) return
    setPreset(activeFilter.preset)
    setCustomFrom(activeFilter.date_from || toDateInputValue(new Date()))
    setCustomTo(activeFilter.date_to || toDateInputValue(new Date()))
    setSelectedCategories(activeFilter.category ?? [])
    setRangeError('')
  }, [showFilter, activeFilter])

  useEffect(() => {
    setIsAllChecked(categories.length > 0 && selectedCategories.length === categories.length)
  }, [selectedCategories, categories])

  const fetchCategoriesData = async () => {
    setLoadingCategories(true)
    try {
      const response: any = await restRequest.getGiftFilterCategories()
      const data = response?.data?.data?.categories ?? []
      setCategories(data.map((item: string) => ({value: item, label: item})))
    } catch {
      setCategories([])
    } finally {
      setLoadingCategories(false)
    }
  }

  useEffect(() => {
    if (showFilter) fetchCategoriesData()
  }, [showFilter])

  const handlePresetChange = (id: GiftCardDatePreset) => {
    setPreset(id)
    setRangeError('')
    if (id !== 'custom') {
      const range = getRangeForPreset(id)
      setCustomFrom(range.date_from)
      setCustomTo(range.date_to)
    }
  }

  const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value, checked} = e.target
    setSelectedCategories((prev) =>
      checked ? [...prev, value] : prev.filter((status) => status !== value)
    )
  }

  const handleAllCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {checked} = e.target
    if (checked) {
      setSelectedCategories(categories.map((option) => option.value))
    } else {
      setSelectedCategories([])
    }
  }

  const handleFilterData = (e: React.FormEvent) => {
    e.preventDefault()
    if (preset === 'custom' && customFrom && customTo && customFrom > customTo) {
      setRangeError('From date must be on or before To date.')
      return
    }
    const range = getRangeForPreset(
      preset,
      preset === 'custom' ? customFrom : undefined,
      preset === 'custom' ? customTo : undefined
    )
    onApply({
      ...range,
      category: selectedCategories,
    })
    toggle()
  }

  return (
    <Modal show={showFilter} className='modal-md' onHide={toggle}>
      <Modal.Header className='bg-primary-subtle' closeButton>
        <Modal.Title>Filter gift card redemptions</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleFilterData}>
          <label className='form-label fw-semibold'>Date range</label>
          <select
            className='form-select mb-4'
            value={preset}
            onChange={(e) => handlePresetChange(e.target.value as GiftCardDatePreset)}
          >
            {GIFT_CARD_DATE_PRESET_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>

          {preset === 'custom' && (
            <div className='row g-4 mb-4'>
              <div className='col-6'>
                <label className='form-label'>From</label>
                <input
                  type='date'
                  className='form-control'
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e) => {
                    setCustomFrom(e.target.value)
                    setRangeError('')
                  }}
                />
              </div>
              <div className='col-6'>
                <label className='form-label'>To</label>
                <input
                  type='date'
                  className='form-control'
                  value={customTo}
                  min={customFrom || undefined}
                  max={toDateInputValue(new Date())}
                  onChange={(e) => {
                    setCustomTo(e.target.value)
                    setRangeError('')
                  }}
                />
              </div>
              {rangeError ? <div className='col-12 text-danger fs-7'>{rangeError}</div> : null}
            </div>
          )}

          <label className='form-label fw-semibold'>Gift card category</label>
          <div className='form-check mt-2 mb-4'>
            <input
              className='form-check-input'
              type='checkbox'
              id='giftCardCheckAll'
              checked={isAllChecked}
              onChange={handleAllCheck}
              disabled={categories.length === 0}
            />
            <label className='form-check-label ms-2' htmlFor='giftCardCheckAll'>
              All categories
            </label>
          </div>
          <div className='row row-cols-2'>
            {loadingCategories ? (
              <div className='col-12 d-flex justify-content-center py-4'>
                <span className='spinner-border text-primary' role='status' />
              </div>
            ) : categories.length > 0 ? (
              categories.map((option) => (
                <div key={option.value} className='mb-3 form-check'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id={`cat-${option.value}`}
                    value={option.value}
                    checked={selectedCategories.includes(option.value)}
                    onChange={handleStatusChange}
                  />
                  <label className='form-check-label ms-2' htmlFor={`cat-${option.value}`}>
                    {option.label}
                  </label>
                </div>
              ))
            ) : (
              <p className='text-muted fs-7 col-12'>No categories available.</p>
            )}
          </div>

          <Modal.Footer className='px-0 pb-0 mt-5'>
            <Button variant='secondary' size='sm' onClick={toggle}>
              Cancel
            </Button>
            <Button type='submit' variant='primary' size='sm'>
              Apply filter
            </Button>
          </Modal.Footer>
        </form>
      </Modal.Body>
    </Modal>
  )
}

export default GiftCardFilter
